import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { createZoneViewColumns } from "./zone-view-columns";
import { statusCalculationMethodData360LiveEnum } from "../../constants/commonConstants";
import { logDataHelperFunctions } from "../../helpers/logdata-helper";
import { extractZoneAndPassForM500FromTableLimits } from "../../helpers/tlog/tlog-data-mapper";
import { zoneViewHelper } from "../../helpers/zone-view/zone-view-helper";
import { FETCH_ZONE_VIEW_SAGA } from "../../redux/d360Live_Views/types";
import EnterpriseGridCommon from "../AgGrid/Ag-Grid";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { useTranslation } from "react-i18next";
import ErrorBanner from "../Common/ErrorBanner";
import CommonLoader from "../Loaders/loader";

const Data360LiveZoneViewAgGrid = ({
  projectId,
  projectName,
  weldNumber,
  statusCalculationMethod,
  currentProject,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const state = useSelector((state) => state?.data360LiveViewsReducer);
  const [rows, setRows] = useState([]);

  const zoneViewData = state?.zoneView?.data || [];
  const isLoading = state?.isLoading?.zoneView;
  const conversionFactors = state?.zoneView?.conversionFactors;
  const decimalPlaces = state?.zoneView?.decimalPlaces;
  const tableLimitRecords = state?.zoneView?.tableLimitRecords;
  const weldParameterErrors = state?.zoneView?.weldParameterErrors;

  const applyConversionFactor = (value, factor, fieldName) => {
    if (!value || value === "") return "";
    const numValue = parseFloat(value);
    const decimalPlacesForField = decimalPlaces?.[fieldName];
    return isNaN(numValue)
      ? value
      : (numValue * (factor || 1)).toFixed(decimalPlacesForField);
  };

  const getStatusCalculationMethod = (statusCalculationMethod, record) => {
    switch (statusCalculationMethod) {
      case statusCalculationMethodData360LiveEnum.Instantaneous:
        return record.zone_status_by_instantaneuous;
      case statusCalculationMethodData360LiveEnum.AvgByPass:
      case statusCalculationMethodData360LiveEnum.AvgByZone:
      case statusCalculationMethodData360LiveEnum.AvgByTilt:
        return record.avg_zone_status;
      default:
        return record.zone_status_by_instantaneuous;
    }
  };

  useEffect(() => {
    if (projectId && projectName) {
      dispatch({
        type: FETCH_ZONE_VIEW_SAGA,
        payload: {
          project_id: projectId,
          project_name: projectName,
          weld_number: weldNumber,
        },
      });
    }
  }, [dispatch, projectId, projectName, weldNumber]);

  useEffect(() => {
    if (zoneViewData?.length) {
      const mappedData = zoneViewData?.map((item, index) => {
        const isM500 =
          item?.welding_system?.toLowerCase() == "m500" ? true : false;

        const m500ZoneDetails = isM500
          ? extractZoneAndPassForM500FromTableLimits(
              tableLimitRecords,
              item.job_number,
              item?.zone_name
            )
          : {};

        return {
          sl_no: index + 1,
          status:
            getStatusCalculationMethod(statusCalculationMethod, item) || "",
          stationNumbers: logDataHelperFunctions.getStationName(
            item?.station_number
          ),
          s_deviceid_event: item?.welduid_s_event_torch || "",
          weld_uuid: item?.welduid_s_event_torch.split("_")[0] || "",
          buyType: item?.bug_type || "",
          type: item?.torch || "",
          zone: isM500 ? m500ZoneDetails.ZoneNum : item?.zone_name || "",
          rawZone: item?.zone_name || "",
          pass: isM500 ? m500ZoneDetails.PassNum : item?.pass_name || "",
          weldStartTimeRaw: item?.s_time || "",
          weld_time: item?.weld_time || "",
          end_time: item?.s_c_time || "",
          autoIncrement: item?.auto_increment || "-",

          //for tlog screen
          bug_number: item?.pass_view?.bug_number,
          band_diameter: applyConversionFactor(
            item?.pass_view?.band_diameter,
            conversionFactors?.band_diameter,
            "band_diameter"
          ),
          wire_diameter: applyConversionFactor(
            item?.pass_view?.wire_diameter,
            conversionFactors?.wire_diameter,
            "wire_diameter"
          ),
          logging_distance: applyConversionFactor(
            item?.pass_view?.logging_distance,
            conversionFactors?.logging_distance,
            "logging_distance"
          ),
          pipe_diameter: applyConversionFactor(
            item?.pass_view?.pipe_diameter,
            conversionFactors?.pipe_diameter,
            "pipe_diameter"
          ),

          ///
          current_low:
            applyConversionFactor(
              item?.current_low,
              conversionFactors?.current,
              "current"
            ) + (item?.current_low_status === false ? "-color" : ""),
          current_avg:
            applyConversionFactor(
              item?.current_avg,
              conversionFactors?.current,
              "current"
            ) + (item?.current_avg_status === false ? "-color" : ""),
          current_high:
            applyConversionFactor(
              item?.current_high,
              conversionFactors?.current,
              "current"
            ) + (item?.current_high_status === false ? "-color" : ""),

          volt_low:
            applyConversionFactor(
              item?.volts_low,
              conversionFactors?.volts,
              "volts"
            ) + (item?.volts_low_status === false ? "-color" : ""),
          volt_avg:
            applyConversionFactor(
              item?.volts_avg,
              conversionFactors?.volts,
              "volts"
            ) + (item?.volts_avg_status === false ? "-color" : ""),
          volt_high:
            applyConversionFactor(
              item?.volts_high,
              conversionFactors?.volts,
              "volts"
            ) + (item?.volts_high_status === false ? "-color" : ""),

          travel_speed_low:
            applyConversionFactor(
              item?.travel_speed_low,
              conversionFactors?.travel_speed,
              "travel_speed"
            ) + (item?.travel_speed_low_status === false ? "-color" : ""),
          travel_speed_avg:
            applyConversionFactor(
              item?.travel_speed_avg,
              conversionFactors?.travel_speed,
              "travel_speed"
            ) + (item?.travel_speed_avg_status === false ? "-color" : ""),
          travel_speed_high:
            applyConversionFactor(
              item?.travel_speed_high,
              conversionFactors?.travel_speed,
              "travel_speed"
            ) + (item?.travel_speed_high_status === false ? "-color" : ""),

          true_energy_low:
            applyConversionFactor(
              item?.true_energy_low,
              conversionFactors?.true_energy,
              "true_energy"
            ) + (item?.true_energy_low_status === false ? "-color" : ""),
          true_energy_avg:
            applyConversionFactor(
              item?.true_energy_avg,
              conversionFactors?.true_energy,
              "true_energy"
            ) + (item?.true_energy_avg_status === false ? "-color" : ""),
          true_energy_high:
            applyConversionFactor(
              item?.true_energy_high,
              conversionFactors?.true_energy,
              "true_energy"
            ) + (item?.true_energy_high_status === false ? "-color" : ""),

          heat_low:
            applyConversionFactor(
              item?.heat_low,
              conversionFactors?.heat,
              "heat"
            ) + (item?.heat_low_status === false ? "-color" : ""),
          heat_avg:
            applyConversionFactor(
              item?.heat_avg,
              conversionFactors?.heat,
              "heat"
            ) + (item?.heat_avg_status === false ? "-color" : ""),
          heat_high:
            applyConversionFactor(
              item?.heat_high,
              conversionFactors?.heat,
              "heat"
            ) + (item?.heat_high_status === false ? "-color" : ""),

          wire_speed_low:
            applyConversionFactor(
              item?.wire_speed_low,
              conversionFactors?.wire_speed,
              "wire_speed"
            ) + (item?.wire_speed_low_status === false ? "-color" : ""),
          wire_speed_avg:
            applyConversionFactor(
              item?.wire_speed_avg,
              conversionFactors?.wire_speed,
              "wire_speed"
            ) + (item?.wire_speed_avg_status === false ? "-color" : ""),
          wire_speed_high:
            applyConversionFactor(
              item?.wire_speed_high,
              conversionFactors?.wire_speed,
              "wire_speed"
            ) + (item?.wire_speed_high_status === false ? "-color" : ""),

          oscillation_width_low:
            applyConversionFactor(
              item?.oscillation_width_low,
              conversionFactors?.oscillation_width,
              "oscillation_width"
            ) + (item?.oscillation_width_low_status === false ? "-color" : ""),
          oscillation_width_avg:
            applyConversionFactor(
              item?.oscillation_width_avg,
              conversionFactors?.oscillation_width,
              "oscillation_width"
            ) + (item?.oscillation_width_avg_status === false ? "-color" : ""),
          oscillation_width_high:
            applyConversionFactor(
              item?.oscillation_width_high,
              conversionFactors?.oscillation_width,
              "oscillation_width"
            ) + (item?.oscillation_width_high_status === false ? "-color" : ""),

          frequency_low:
            applyConversionFactor(
              item?.frequency_low,
              conversionFactors?.frequency,
              "frequency"
            ) + (item?.frequency_low_status === false ? "-color" : ""),
          frequency_avg:
            applyConversionFactor(
              item?.frequency_avg,
              conversionFactors?.frequency,
              "frequency"
            ) + (item?.frequency_avg_status === false ? "-color" : ""),
          frequency_high:
            applyConversionFactor(
              item?.frequency_high,
              conversionFactors?.frequency,
              "frequency"
            ) + (item?.frequency_high_status === false ? "-color" : ""),

          target_low:
            applyConversionFactor(
              item?.target_low,
              conversionFactors?.target,
              "target"
            ) + (item?.target_low_status === false ? "-color" : ""),
          target_avg:
            applyConversionFactor(
              item?.target_avg,
              conversionFactors?.target,
              "target"
            ) + (item?.target_avg_status === false ? "-color" : ""),
          target_high:
            applyConversionFactor(
              item?.target_high,
              conversionFactors?.target,
              "target"
            ) + (item?.target_high_status === false ? "-color" : ""),

          horizontal_bias_low:
            applyConversionFactor(
              item?.horizontal_bias_low,
              conversionFactors?.horizontal_bias,
              "horizontal_bias"
            ) + (item?.horizontal_bias_low_status === false ? "-color" : ""),
          horizontal_bias_avg:
            applyConversionFactor(
              item?.horizontal_bias_avg,
              conversionFactors?.horizontal_bias,
              "horizontal_bias"
            ) + (item?.horizontal_bias_avg_status === false ? "-color" : ""),
          horizontal_bias_high:
            applyConversionFactor(
              item?.horizontal_bias_high,
              conversionFactors?.horizontal_bias,
              "horizontal_bias"
            ) + (item?.horizontal_bias_high_status === false ? "-color" : ""),

          total_wire_consumed_low:
            applyConversionFactor(
              item?.total_wire_consumed_low,
              conversionFactors?.total_wire_consumed,
              "total_wire_consumed"
            ) +
            (item?.total_wire_consumed_low_status === false ? "-color" : ""),
          total_wire_consumed_avg:
            applyConversionFactor(
              item?.total_wire_consumed_avg,
              conversionFactors?.total_wire_consumed,
              "total_wire_consumed"
            ) +
            (item?.total_wire_consumed_avg_status === false ? "-color" : ""),
          total_wire_consumed_high:
            applyConversionFactor(
              item?.total_wire_consumed_high,
              conversionFactors?.total_wire_consumed,
              "total_wire_consumed"
            ) +
            (item?.total_wire_consumed_high_status === false ? "-color" : ""),

          arc_impedance_low:
            applyConversionFactor(
              item?.arc_impedance_low,
              conversionFactors?.arc_impedance,
              "arc_impedance"
            ) + (item?.arc_impedance_low_status === false ? "-color" : ""),
          arc_impedance_avg:
            applyConversionFactor(
              item?.arc_impedance_avg,
              conversionFactors?.arc_impedance,
              "arc_impedance"
            ) + (item?.arc_impedance_avg_status === false ? "-color" : ""),
          arc_impedance_high:
            applyConversionFactor(
              item?.arc_impedance_high,
              conversionFactors?.arc_impedance,
              "arc_impedance"
            ) + (item?.arc_impedance_high_status === false ? "-color" : ""),
        };
      });
      setRows(mappedData);
    }
  }, [zoneViewData, conversionFactors]);

  const handleRowClick = (params) => {
    const record = params.data;
    history.push("/data360-live-tlog-data", {
      stationNumbers: record.stationNumbers,
      buyType: record.buyType,
      torch: record.type,
      weld_number: weldNumber,
      s_deviceid_event: record.s_deviceid_event,
      zone: record.zone,
      rawZone: record.rawZone,
      view: "Zone",

      //tlog display
      autoIncrement: record.autoIncrement,
      band_diameter: record.band_diameter,
      logging_distance: record.logging_distance,
      bug_number: record.bug_number,
      wire_diameter: record.wire_diameter,
      pipe_diameter: record.pipe_diameter,
      start_time: record.weldStartTimeRaw,
      end_time: record.end_time,
      weld_time: record.weld_time,

      // gps: record.gps,
      // iwm: record.iwm,
      // isM500: record.isM500,
    });
  };

  const onRowClicked = (params) => {
    console.log("Row clicked", params);
    handleRowClick(params);
  };

  const processCellCallback = (params) => {
    if (params?.column?.colDef?.processCellForExport) {
      return params.column.colDef.processCellForExport(params);
    }
    return params.value;
  };

  // Extract error messages from weldParameterErrors
  const getErrorMessages = () => {
    if (
      !weldParameterErrors?.errors ||
      weldParameterErrors.errors.length === 0
    ) {
      return [];
    }

    const errorMessages = [];
    weldParameterErrors.errors.forEach((error) => {
      if (error.parsedErrors?.errorMessages) {
        errorMessages.push(...error.parsedErrors.errorMessages);
      }
    });

    return errorMessages;
  };

  const errorMessages = getErrorMessages();

  return (
    <div className="pass-zone">
      {isLoading ? (
        <div className="slogdata-loader">
          <CommonLoader />
        </div>
      ) : (
        <>
          <ErrorBanner errors={errorMessages} />
          {rows.length > 0 ? (
            <div className="ag-theme-alpine" style={{ width: "100%" }}>
              <EnterpriseGridCommon
                isLoading={isLoading}
                rows={rows}
                columnDefs={zoneViewHelper.mapZoneViewColumnTitles(
                  createZoneViewColumns(t),
                  logDataHelperFunctions.data360LiveGetReportColumns(
                    currentProject
                  )
                )}
                onRowClicked={onRowClicked}
                exportFileName={"zone-view"}
                processCellCallback={processCellCallback}
                heightValue={-150}
              />
            </div>
          ) : (
            <div className="slog-NoData">
              <p>{t("sLogData.noDataAvailable")}</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Data360LiveZoneViewAgGrid;
